import { Edit01 } from '@carespace-icons/general/edit01';
import { InfoCircle } from '@carespace-icons/general/infoCircle';
import { XClose } from '@carespace-icons/general/xClose';
import { patchRomBodyPoints } from '@stores/rom/customRom';
import { useTypedDispatch, useTypedSelector } from '@stores/index';
import {
	ICustomRomBodyPoints,
	ICustomRomExercise,
	ICustomRomSessionExercise,
} from '@stores/interfaces';
import {
	Card,
	Image,
	Input,
	List,
	message,
	Progress,
	Tooltip
} from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { MdOutlineRemoveRedEye } from 'react-icons/md';
import { Check } from '@carespace-icons/general/check';

import './style.css';
interface ICustomSummaryContent {
	item: ICustomRomSessionExercise[];
}

const CustomSummaryContentResult = (props: ICustomSummaryContent) => {
	const { item } = props;
	const { t } = useTranslation();
	const { user } = useTypedSelector(state => ({
		user: state.user,
	}));

	const [editedPostures, setEditedPostures] = useState<{
		[key: string]: ICustomRomBodyPoints;
	}>({});
	const [labels, setLabels] = useState<string[]>([]);
	const [itemData, setItemData] = useState<unknown>(item);
	const handleEdit = (index: number, postureId: string) => {
		setEditedPostures({
			...editedPostures,
			[postureId]: {
				...itemData[index].results.find(
					(p: { id: string }) => p.id === postureId,
				),
			},
		});
	};

	useEffect(() => {
		const uniqueItems = new Map();
		item.forEach(exercise => {
			const title = exercise?.title || 'Unknown';
			if (
				!uniqueItems.has(title) ||
				new Date(exercise.updatedAt) >
					new Date(uniqueItems.get(title).updatedAt)
			) {
				uniqueItems.set(title, exercise);
			}
		});
		setItemData(Array.from(uniqueItems.values()));
	}, [item]);

	const handleCancel = (postureId: string) => {
		const newEditedPostures = { ...editedPostures };
		delete newEditedPostures[postureId];
		setEditedPostures(newEditedPostures);
	};
	const dispatch = useTypedDispatch();
	const handleSave = async (postureId: string) => {
		const payload = {
			normal: editedPostures[postureId].normal,
			wfl: editedPostures[postureId].wfl,
			min: editedPostures[postureId].min,
			max: editedPostures[postureId].max,
			value: editedPostures[postureId].value,
		};
		await dispatch(
			patchRomBodyPoints({
				bodyPointId: editedPostures[postureId].id,
				payload: payload,
			}),
		);
		handleCancel(postureId);
		const updatedItems = itemData.map((exercise: ICustomRomExercise) => {
			if (exercise.results) {
				return {
					...exercise,
					results: exercise.results.map((bodyPoint: ICustomRomBodyPoints) =>
						bodyPoint.id === postureId
							? { ...bodyPoint, ...editedPostures[postureId] }
							: bodyPoint,
					),
				};
			}
			return exercise;
		});
		setItemData(updatedItems);
		message.success(t('Patient.data.omnirom.updateSuccess'));
	};

	const handleInputChange = (
		postureId: string,
		field: string,
		value: number,
	) => {
		setEditedPostures(prevState => ({
			...prevState,
			[postureId]: {
				...prevState[postureId],
				[field]: value,
			},
		}));
	};

	const calculateScore = (
		normal: number,
		value: number,
		id: number,
	): number => {
		if (id !== 21 && id !== 17 && id !== 4 && id !== 11) {
			if (value === 0) {
				return 0;
			}
			const score = Math.round((value / normal) * 100);
			return isFinite(score) ? score : 0;
		} else {
			if (value === 0) {
				return 100;
			}
			const score = Math.round(100 - (value / 100) * 100);
			return isFinite(score) ? score : 0;
		}
	};

	const handleRender = (scrnshot: string) => {
		return (
			<Image
				width={64}
				height={53}
				src={scrnshot}
				preview={{
					src: scrnshot,
					mask: <MdOutlineRemoveRedEye size={18} />,
					width: 'auto',
					height: 'auto',
				}}
				style={{ borderRadius: '0.5rem' }}
				onError={e => {
					const target = e.target as HTMLImageElement;
					target.src = '/images/white-image.png';
				}}
			/>
		);
	};

	const calculateRanking = (score: number): number => {
		if (score >= 91) return 10;
		if (score >= 81) return 9;
		if (score >= 71) return 8;
		if (score >= 61) return 7;
		if (score >= 51) return 6;
		if (score >= 41) return 5;
		if (score >= 31) return 4;
		if (score >= 21) return 3;
		if (score >= 11) return 2;
		if (score >= 1) return 1;
		return 0;
	};

	const rankingColor = (ranking: number) => {
		if (ranking >= 1 && ranking <= 6) return '#F04438';
		if (ranking >= 7 && ranking <= 9) return '#FDB022';
		if (ranking === 10) return '#3CCB7F';
		return 'gray';
	};

	useEffect(() => {
		if (user?.isPhysioterapist) {
			setLabels(['normal', 'wfl', 'value', 'score', 'ranking', 'edit']);
		} else {
			setLabels(['normal', 'wfl', 'value', 'score', 'ranking']);
		}
	}, [user]);

	return (
		<div className="rom-card-container p-0">
			{itemData?.map((exercise: ICustomRomExercise, idx: number) => (
				<>
					{exercise?.results.length > 0 ? (
						<Card
							key={exercise.id}
							className="MCardResults posture-card-section"
							size="small"
							title={
								<div className="posture-title-container">
									<p
										className="posture-title"
										style={{
											display: 'flex',
											alignItems: 'center',
											gap: '10px',
										}}>
										{exercise?.results.length > 1 &&
											handleRender(exercise?.screenshot)}
										<p>
											{(
												exercise?.title ||
												exercise?.strapiOmniRomExercise?.name ||
												exercise?.exerciseLibrary?.title ||
												exercise?.romProgramExercise?.strapiOmniRomExercise
													?.name ||
												exercise?.romProgramExercise?.exerciseLibrary?.title ||
												exercise?.results[0]?.romProgramExercise
													?.exerciseLibrary?.title ||
												exercise?.romProgramExercise?.exerciseLibrary?.title ||
												exercise?.results[0]?.romProgramExercise
													?.strapiOmniRomExercise?.name ||
												exercise?.results[0]?.strapiOmniRomExercise?.name ||
												'No Title'
											).toUpperCase()}
										</p>
									</p>
									<div
										className="posture-label-container"
										style={{
											width: user.isPhysioterapist ? '500px' : '415px',
											paddingLeft: '5px',
										}}>
										{labels.map(label => (
											<div key={label} className="posture-label">
												<p
													className="posture-label-text"
													style={{
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
													}}>
													{label === 'wfl'
														? label.toUpperCase()
														: label.charAt(0).toUpperCase() +
															label.slice(1).toLowerCase()}
													{(label === 'wfl' || label === 'normal') && (
														<Tooltip
															placement="topLeft"
															title={
																<span className="text-gray-700 font-semibold !p-2">
																	{label === 'wfl'
																		? t(
																				'Patient.data.myProgress.omniRom.wflToolTip',
																			)
																		: t(
																				'Patient.data.myProgress.omniRom.normalToolTip',
																			)}
																</span>
															}
															color="white"
															className="text-gray-700 font-semibold">
															<div className="ml-1">
																<InfoCircle
																	width={16}
																	height={16}
																	color="stroke-gray-600"
																/>
															</div>
														</Tooltip>
													)}
												</p>
											</div>
										))}
									</div>
								</div>
							}>
							<List>
								{exercise.results?.map((posture: ICustomRomBodyPoints) => {
									const score = calculateScore(
										posture.normal || posture.romProgramExercise.normal,
										posture.value,
										posture.romProgramExercise.strapiOmniRomExerciseId,
									);
									const ranking = calculateRanking(score);

									return (
										<div key={posture.id} className="posture-sides-container">
											<div className="posture-sides-section">
												{exercise?.results.length <= 1 ? (
													handleRender(exercise?.screenshot)
												) : (
													<p className="posture-sides-title">
														{posture?.strapiOmniRomExercise?.name ||
															posture?.romProgramExercise?.strapiOmniRomExercise
																?.name ||
															posture?.romProgramExercise?.exerciseLibrary
																?.title ||
															posture?.strapiOmniRomExercise?.name ||
															posture?.exerciseLibrary?.title ||
															'Standard Motion'}
													</p>
												)}
											</div>
											<div className="posture-side-labels-container">
												{labels.map(label => {
													if (label === 'edit') {
														return (
															<div
																key={label}
																className="posture-side-labels-section">
																{editedPostures[posture.id] ? (
																	<div className="flex gap-2">
																		<Tooltip title="Cancel">
																			<div
																				style={{ cursor: 'pointer' }}
																				onClick={() =>
																					handleCancel(posture.id)
																				}>
																				<XClose color="stroke-rose-500" />
																			</div>
																		</Tooltip>
																		<Tooltip title="Save">
																			<div
																				style={{ cursor: 'pointer' }}
																				onClick={() => handleSave(posture.id)}>
																				<Check color="stroke-green-500" />
																			</div>
																		</Tooltip>
																	</div>
																) : (
																	<Tooltip title="Edit">
																		<div
																			style={{ cursor: 'pointer' }}
																			onClick={() =>
																				handleEdit(idx, posture.id)
																			}>
																			<Edit01 color="stroke-gray-400" />
																		</div>
																	</Tooltip>
																)}
															</div>
														);
													}
													const scoreColor = () => {
														if (score >= 90) return '#32D583';
														if (score >= 60 && score < 90) return '#FDB022';
														if (score >= 0 && score < 60) return '#F04438';
														return 'gray';
													};
													return (
														<div
															key={label}
															className="posture-side-labels-section">
															{editedPostures[posture.id] ? (
																label === 'score' || label === 'ranking' ? (
																	<p>
																		{label === 'score' ? (
																			<Progress
																				type="circle"
																				percent={score || 0}
																				strokeColor={scoreColor()}
																				width={50}
																				format={percent => (
																					<span
																						style={{
																							color: scoreColor(),
																							fontWeight: 'bold',
																						}}>
																						{percent}%
																					</span>
																				)}
																			/>
																		) : label === 'ranking' ? (
																			<span
																				style={{
																					color: rankingColor(ranking),
																				}}>
																				{ranking}
																			</span>
																		) : label === 'value' ? (
																			<span style={{ color: scoreColor() }}>
																				{posture[label]}
																			</span>
																		) : (
																			posture[label] || 0
																		)}
																	</p>
																) : (
																	<Input
																		value={
																			editedPostures[posture.id]?.[label] || 0
																		}
																		type="number"
																		style={{ width: '70px' }}
																		onChange={e =>
																			handleInputChange(
																				posture.id,
																				label,
																				Number(e.target.value),
																			)
																		}
																	/>
																)
															) : (
																<p>
																	{label === 'score' ? (
																		<Progress
																			type="circle"
																			percent={score || 0}
																			strokeColor={scoreColor()}
																			width={50}
																			format={percent => (
																				<span
																					style={{
																						color: scoreColor(),
																						fontWeight: 'bold',
																					}}>
																					{percent}%
																				</span>
																			)}
																		/>
																	) : label === 'ranking' ? (
																		<span
																			style={{ color: rankingColor(ranking) }}>
																			{ranking}
																		</span>
																	) : label === 'value' ? (
																		<span style={{ color: scoreColor() }}>
																			{posture[label]}
																		</span>
																	) : (
																		posture[label] || 0
																	)}
																</p>
															)}
														</div>
													);
												})}
											</div>
										</div>
									);
								})}
							</List>
						</Card>
					) : (
						<>
							<div className="coach-rom-summary-container-row-div">
								<img
									style={{ width: 100, height: 100 }}
									className="coach-rom-summary-container-row-div-img"
									src="/images/NoExe.png"
									alt="No Exercises Image"
								/>
								<h1 className="coach-rom-summary-container-row-div-h1">
									{t('Patient.data.myProgress.omniRom.empty')}
								</h1>
							</div>
						</>
					)}
				</>
			))}
		</div>
	);
};

export default CustomSummaryContentResult;
